/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
      window.SearchInputSync?.scheduleLoadSync();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      window.SearchInputSync?.syncFrom(this.input, this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Whether this instance is the mobile drawer header search.
     * @returns {boolean}
     */
    isMobileHeaderSearch() {
      return this.input?.id === 'header-search-mob';
    }

    /**
     * Smoothly scrolls the mobile drawer search input into view on focus.
     */
    scrollMobileSearchIntoView() {
      const target = this.querySelector('.search__form') || this.input;
      const menuContent = this.closest('.main-menu__content');
      if (!target) return;

      requestAnimationFrame(() => {
        if (menuContent) {
          const menuTop = menuContent.getBoundingClientRect().top;
          const targetTop = target.getBoundingClientRect().top;
          const scrollTop = menuContent.scrollTop + targetTop - menuTop - 16;

          menuContent.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth',
          });
          return;
        }

        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }

    /**
     * Handles 'focus' events on the search field.
     * @param {FocusEvent} evt - Event object.
     */
    handleFocus() {
      if (this.isMobileHeaderSearch()) {
        this.scrollMobileSearchIntoView();
      }

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (!this.hasAttribute('open')) this.open();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        if (!this.hasAttribute('open')) this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Whether this instance is in the main header search bar.
     * @returns {boolean}
     */
    isHeaderSearch() {
      return !!this.closest('.header__search:not(.header__search--in-mob-menu)');
    }

    /**
     * Collapses the minimised header search bar when visible.
     */
    closeHeaderSearchBar() {
      const storeHeader = this.closest('store-header');
      if (!storeHeader?.classList.contains('search-is-visible')) return;
      storeHeader.handleSearchClose({ preventDefault: () => {} });
    }

    /**
     * Closes search when clicking outside the header search area.
     * @param {PointerEvent} evt - Event object.
     */
    handleOutsidePointerDown(evt) {
      const headerSearch = this.isHeaderSearch() ? this.closest('.header__search') : null;

      if (headerSearch) {
        const searchForm = this.querySelector('.search__form');
        const results = this.querySelector('.predictive-search');

        if (this.overlay?.contains(evt.target)) {
          if (this.hasAttribute('open')) this.close();
          this.closeHeaderSearchBar();
          return;
        }

        if (searchForm?.contains(evt.target) || results?.contains(evt.target)) return;
        if (headerSearch.contains(evt.target)) return;

        if (this.hasAttribute('open')) this.close();
        this.closeHeaderSearchBar();
        return;
      }

      if (!this.hasAttribute('open')) return;

      const modal = this.querySelector('.predictive-search');
      const searchForm = this.querySelector('.search__form');
      if (modal?.contains(evt.target) || searchForm?.contains(evt.target)) return;

      this.close();
    }

    /**
     * Whether this instance lives inside the main page content area.
     * @returns {boolean}
     */
    isInMainContent() {
      return !!this.closest('#main-content');
    }

    /**
     * Target scroll position that places the search bar below the header.
     * @returns {number}
     */
    getSearchBarScrollTop() {
      const target = this.closest('.search__form') || this.input;
      if (!target) return window.scrollY;

      const headerOffset = (this.isHeaderSearch() ? parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) : 0) || 0;
      return Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset - 8);
    }

    /**
     * Cancels any in-progress animated scroll to the search bar.
     */
    cancelScrollToSearch() {
      if (this.scrollToSearchFallback) {
        clearTimeout(this.scrollToSearchFallback);
        this.scrollToSearchFallback = null;
      }

      if (this.duringScrollHandler) {
        document.removeEventListener('touchmove', this.duringScrollHandler);
        document.removeEventListener('wheel', this.duringScrollHandler);
        this.duringScrollHandler = null;
      }

      if (this.scrollEndHandler) {
        window.removeEventListener('scrollend', this.scrollEndHandler);
        this.scrollEndHandler = null;
      }
    }

    /**
     * Scrolls the search bar into view when results open.
     * @returns {Promise<void>}
     */
    scrollToSearchBar() {
      // if (!this.isInMainContent()) {
      //   return Promise.resolve();
      // }

      const targetTop = this.getSearchBarScrollTop() - 20;

      if (Math.abs(window.scrollY - targetTop) < 5) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const finish = () => {
          this.cancelScrollToSearch();
          resolve();
        };

        this.duringScrollHandler = (evt) => {
          const scroller = evt.target.closest('.predictive-search__scroller');
          if (scroller?.contains(evt.target)) return;
          evt.preventDefault();
        };
        document.addEventListener('touchmove', this.duringScrollHandler, { passive: false });
        document.addEventListener('wheel', this.duringScrollHandler, { passive: false });

        this.scrollEndHandler = finish;
        window.addEventListener('scrollend', this.scrollEndHandler, { once: true });
        this.scrollToSearchFallback = setTimeout(finish, 700);

        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      });
    }

    /**
     * Prevents page scroll while allowing results list to scroll.
     * @param {Event} evt - Event object.
     */
    preventPageScroll(evt) {
      if (!this.pageScrollLocked) return;

      const scroller = this.querySelector('.predictive-search__scroller');
      if (scroller?.contains(evt.target)) return;

      evt.preventDefault();
    }

    /**
     * Prevents mobile menu scroll while allowing results list to scroll.
     * @param {Event} evt - Event object.
     */
    preventMainMenuScroll(evt) {
      if (evt.target.closest('.predictive-search__scroller')) return;
      evt.preventDefault();
    }

    /**
     * Locks mobile menu scroll while predictive search is open.
     */
    lockMainMenuScroll() {
      if (this.mainMenuScrollLocked || !document.body.classList.contains('overlay-predictive-search')) {
        return;
      }

      const mainMenuContent = document.getElementById('MainMenuContent');
      if (!mainMenuContent) return;

      this.mainMenuContent = mainMenuContent;
      this.mainMenuScrollTop = mainMenuContent.scrollTop;
      mainMenuContent.style.overflow = 'hidden';

      this.preventMainMenuScrollHandler = this.preventMainMenuScrollHandler
        || this.preventMainMenuScroll.bind(this);
      mainMenuContent.addEventListener('touchmove', this.preventMainMenuScrollHandler, { passive: false });
      mainMenuContent.addEventListener('wheel', this.preventMainMenuScrollHandler, { passive: false });

      this.mainMenuScrollLocked = true;
    }

    /**
     * Restores mobile menu scroll after predictive search closes.
     */
    unlockMainMenuScroll() {
      if (!this.mainMenuScrollLocked || !this.mainMenuContent) return;

      const { mainMenuContent, mainMenuScrollTop } = this;

      mainMenuContent.style.overflow = '';

      if (this.preventMainMenuScrollHandler) {
        mainMenuContent.removeEventListener('touchmove', this.preventMainMenuScrollHandler);
        mainMenuContent.removeEventListener('wheel', this.preventMainMenuScrollHandler);
      }

      mainMenuContent.scrollTop = mainMenuScrollTop || 0;
      this.mainMenuScrollLocked = false;
      this.mainMenuContent = null;
    }

    /**
     * Locks page scroll while search results are open.
     */
    lockPageScroll() {
      if (this.pageScrollLocked) return;

      this.scrollPosition = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';

      this.preventPageScrollHandler = this.preventPageScrollHandler
        || this.preventPageScroll.bind(this);
      document.addEventListener('touchmove', this.preventPageScrollHandler, { passive: false });
      document.addEventListener('wheel', this.preventPageScrollHandler, { passive: false });

      this.pageScrollLocked = true;
    }

    /**
     * Restores page scroll after search closes.
     */
    unlockPageScroll() {
      if (!this.pageScrollLocked) return;

      const { scrollPosition } = this;

      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';

      if (this.preventPageScrollHandler) {
        document.removeEventListener('touchmove', this.preventPageScrollHandler);
        document.removeEventListener('wheel', this.preventPageScrollHandler);
      }

      window.scrollTo(0, scrollPosition || 0);
      this.pageScrollLocked = false;
    }

    /**
     * Caps dropdown height so results stay within the viewport.
     */
    positionResults() {
      const form = this.querySelector('.search');
      const scroller = this.querySelector('.predictive-search__scroller');
      if (!form || !scroller) return;

      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const availableHeight = viewportHeight - form.getBoundingClientRect().bottom - 16;
      const maxHeight = Math.max(availableHeight, 0);

      this.style.setProperty('--predictive-search-max-height', `${maxHeight}px`);
      scroller.style.maxHeight = `${maxHeight}px`;
    }

    /**
     * Clears dynamically calculated dropdown height.
     */
    clearPositionResults() {
      this.style.removeProperty('--predictive-search-max-height');

      const scroller = this.querySelector('.predictive-search__scroller');
      if (scroller) scroller.style.maxHeight = '';
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay?.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');
      document.documentElement.classList.add('overlay-predictive-search');
      this.lockMainMenuScroll();

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || ((evt) => {
        this.close(evt);
        this.closeHeaderSearchBar();
      });
      this.outsidePointerDownHandler = this.outsidePointerDownHandler
        || this.handleOutsidePointerDown.bind(this);
      this.resizeHandler = this.resizeHandler || (() => {
        // if (this.hasAttribute('open')) this.positionResults();
      });

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay?.addEventListener('click', this.overlayClickHandler);
      document.addEventListener('pointerdown', this.outsidePointerDownHandler, true);
      window.addEventListener('resize', this.resizeHandler);

      requestAnimationFrame(() => {
        // this.positionResults();

        if (!this.pageScrollLocked) {
          this.scrollToSearchBar().then(() => {
            if (!this.hasAttribute('open') || this.pageScrollLocked) return;
            this.lockPageScroll();
            // this.positionResults();
          });
        }
      });
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.cancelScrollToSearch();

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        window.SearchInputSync?.syncFrom(this.input, '');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay?.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');
      document.documentElement.classList.remove('overlay-predictive-search');
      this.unlockPageScroll();
      this.unlockMainMenuScroll();

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay?.removeEventListener('click', this.overlayClickHandler);

      if (this.outsidePointerDownHandler) {
        document.removeEventListener('pointerdown', this.outsidePointerDownHandler, true);
      }

      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }

      this.clearPositionResults();
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
